import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import SubAgentDetailsPanel from './SubAgentDetailsPanel';
import T2AgentPanel from './T2AgentPanel';
import AGMAgentPanel from './AGMAgentPanel';
import GstPstAgentPanel from './GstPstAgentPanel';
import AuditorHelpPanel from './AuditorHelpPanel';
import CorporateRecordsPanel from './CorporateRecordsPanel';

const STATUS_ORDER = ['needs_attention', 'in_progress', 'pending', 'complete'];
const PRIORITY_ORDER = ['critical', 'required', 'recommended'];

/** Fixed Accountant slots */
const ACCOUNTANT_COMPANY_REGISTER_PATH = 'expenses/COMPANY_COST_REGISTER.xlsx';
/** Sub-agent id for OptiPrime T2 (backend still uses `tax`). */
const T2_SUBAGENT_ID = 'tax';
/** Sub-agent id for annual general meeting workflow. */
const AGM_SUBAGENT_ID = 'agm';
/** Sub-agent id for GST & PST sub-auditor. */
const GST_PST_SUBAGENT_ID = 'gst_pst';
/** Sub-agent id for the deterministic, grounded Help agent. */
const HELP_SUBAGENT_ID = 'help';
/** Vendor invoice categories shown under the Accountant agent */
const VENDOR_CATEGORIES = [
  { id: 'hetzner',    label: 'HETZNER',    match: (d) => /hetzner/i.test(`${d.label || ''} ${d.file_path || ''}`) },
  { id: 'porkbun',    label: 'PORKBUN',    match: (d) => /porkbun/i.test(`${d.label || ''} ${d.file_path || ''}`) },
  { id: 'anthropic',  label: 'ANTHROPIC',  match: (d) => /anthropic/i.test(`${d.label || ''} ${d.file_path || ''}`) },
  { id: 'openai',     label: 'OPENAI',     match: (d) => /openai/i.test(`${d.label || ''} ${d.file_path || ''}`) },
  { id: 'cursor',     label: 'CURSOR',     match: (d) => /cursor/i.test(`${d.label || ''} ${d.file_path || ''}`) },
  {
    id: 'compliance',
    label: 'COMPLIANCE',
    match: (d) =>
      /compliance|incorporation|articles|cbca|resolution|registry|certificate|minute\s*book|corporate/i.test(
        `${d.label || ''} ${d.file_path || ''}`
      ),
  },
];

function isBankStatementPath(fp) {
  return /^expenses\/bank_statements\/\d{4}-\d{2}\.pdf$/.test(fp || '');
}

/**
 * Open /opo/document/:id in a new tab via window.open so window.opener is set (unlike <a target="_blank">, which is noopener by default).
 * SpreadsheetViewerPage "Close" then calls window.close() instead of navigating /opo in the viewer tab.
 */
function openDocumentViewerTab(documentId, e) {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  e.preventDefault();
  window.open(`/opo/document/${documentId}`, '_blank');
}

/** Default landing panel when Accountant is selected with no document open. */
function AccountantInstructionsPanel({ companyCostRegisterDoc, onOpenWorkbook }) {
  return (
    <div className="accountant-instructions-panel">
      <h3 className="accountant-instructions-title">Accountant — getting started</h3>
      <p className="accountant-instructions-lead">
        Upload and reconcile expenses through the <strong>ASPE Compliant Preparation Record</strong> workbook.
        Bank statements, vendor invoices, and ASPE financials all flow from there into the OptiPrime T2 agent.
      </p>
      <ol className="accountant-instructions-steps">
        <li>
          <strong>Open the preparation record</strong> — click{' '}
          <strong>ASPE Compliant Preparation Record</strong> in the sidebar (or{' '}
          {companyCostRegisterDoc ? (
            <button type="button" className="accountant-instructions-link" onClick={onOpenWorkbook}>
              open workbook
            </button>
          ) : (
            'open workbook'
          )}
          ).
        </li>
        <li>
          <strong>CIBC business account</strong> — in the workbook, open the <code className="auditor-inline-code">CIBC POSITION</code>{' '}
          tab. Click a month chip, upload the PDF (⬆), review extracted transactions, and set allocation / paid-by.
        </li>
        <li>
          <strong>BMO personal account (Fred)</strong> — open the <code className="auditor-inline-code">BMO_POSITION</code> tab.
          Upload each monthly statement, tick rows as OptiPrime-related, and sync to the cash-flow ledger.
        </li>
        <li>
          <strong>Vendor invoices</strong> — use the category tiles in the sidebar (Hetzner, Porkbun, etc.) or{' '}
          <strong>+ Add document</strong> for receipts and misc files saved under <code className="auditor-inline-code">01_auditor/expenses/</code>.
        </li>
        <li>
          <strong>Refresh the workbook</strong> — after uploads, click <strong>Refresh workbook</strong> in the preparation
          record to sync Balance Sheet, Statement of Operations, and founder loans from bank-truth data.
        </li>
      </ol>
      <p className="accountant-instructions-foot">
        Ask below for help with a specific month, allocation, or T2 checklist item.
      </p>
    </div>
  );
}

/** Vendor invoice grids: sort pill boxes chronologically (label + file_path). */
const VENDOR_IDS_CHRONO_SORT = new Set(['hetzner', 'porkbun', 'anthropic', 'openai', 'cursor']);

const MONTH_TOKEN_TO_INDEX = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
};

const MONTH_NAME_PATTERN =
  'jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec';

function normInvoiceHay(doc) {
  const raw = `${doc.label || ''}\n${doc.file_path || ''}`;
  return raw.replace(/^[\s"']+/gm, '').trim();
}

/** DB upload timestamp (from accountant_linked_documents.created_at) — used when label has no invoice date. */
function parseDocCreatedAtMs(doc) {
  if (!doc || doc.created_at == null || String(doc.created_at).trim() === '') return null;
  let s = String(doc.created_at).trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) s = `${s.replace(' ', 'T')}Z`;
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
}

/**
 * Single numeric sort key (ms UTC). Later dates sort after earlier ones.
 * Uses explicit ISO dates first, then month/day/year from labels, then
 * "Month N" subscription indices, then upload time (created_at), then id.
 */
function invoiceChronologicalSortKeyMs(doc) {
  const hay = normInvoiceHay(doc);
  if (!hay) return Number.MAX_SAFE_INTEGER;

  const candidates = [];

  const pushUtc = (y, m0, d) => {
    const t = Date.UTC(y, m0, d);
    if (!Number.isNaN(t)) candidates.push(t);
  };

  // 1) ISO YYYY-MM-DD (take every match; invoice may list period end)
  for (const m of hay.matchAll(/\b(20\d{2})-(\d{2})-(\d{2})\b/g)) {
    pushUtc(+m[1], +m[2] - 1, +m[3]);
  }

  // 2) Compact YYYYMMDD in filenames
  for (const m of hay.matchAll(/\b(20\d{2})(\d{2})(\d{2})\b/g)) {
    const y = +m[1];
    const mo = +m[2];
    const d = +m[3];
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) pushUtc(y, mo - 1, d);
  }

  // 2b) YYYYMMDD glued to letters (e.g. invoice20250315.pdf — \b does not break letter↔digit)
  for (const m of hay.matchAll(/(?:^|[^0-9])(20\d{2})(\d{2})(\d{2})(?![0-9])/g)) {
    const y = +m[1];
    const mo = +m[2];
    const d = +m[3];
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) pushUtc(y, mo - 1, d);
  }

  // 3) YYYY-MM (no day) — end of that calendar month (human month 1–12)
  for (const m of hay.matchAll(/\b(20\d{2})-(\d{2})\b(?!-\d{2})/g)) {
    const y = +m[1];
    const humanMo = +m[2];
    if (humanMo >= 1 && humanMo <= 12) candidates.push(Date.UTC(y, humanMo, 0));
  }

  const yearsFound = (hay.match(/\b(20\d{2})\b/g) || []).map((x) => +x);
  const defaultYear = yearsFound.length ? Math.max(...yearsFound) : 2025;

  // 4) "Jul 7", "Jul 7, 2025", "Sept 1"
  const mdyRe = new RegExp(
    `\\b(${MONTH_NAME_PATTERN})\\w*\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s*(20\\d{2}))?\\b`,
    'gi'
  );
  let md;
  while ((md = mdyRe.exec(hay)) !== null) {
    const monKey = md[1].toLowerCase();
    const m0 = MONTH_TOKEN_TO_INDEX[monKey.slice(0, 4) === 'sept' ? 'sept' : monKey.slice(0, 3)];
    if (m0 == null) continue;
    const day = Math.min(31, Math.max(1, +md[2]));
    const y = md[3] ? +md[3] : defaultYear;
    pushUtc(y, m0, day);
  }

  // 5) "Aug usage", "Sep usage"
  const usageRe = new RegExp(`\\b(${MONTH_NAME_PATTERN})\\w*\\s+usage\\b`, 'gi');
  let mu;
  while ((mu = usageRe.exec(hay)) !== null) {
    const monKey = mu[1].toLowerCase();
    const m0 = MONTH_TOKEN_TO_INDEX[monKey.slice(0, 4) === 'sept' ? 'sept' : monKey.slice(0, 3)];
    if (m0 == null) continue;
    pushUtc(defaultYear, m0, 15);
  }

  // 6) "Pro Jan", "Ultra Nov", "Ultra opened …"
  const proRe = new RegExp(`\\b(?:pro|ultra)\\s+(${MONTH_NAME_PATTERN})\\w*\\b`, 'gi');
  let pr;
  while ((pr = proRe.exec(hay)) !== null) {
    const monKey = pr[1].toLowerCase();
    const m0 = MONTH_TOKEN_TO_INDEX[monKey.slice(0, 4) === 'sept' ? 'sept' : monKey.slice(0, 3)];
    if (m0 == null) continue;
    pushUtc(defaultYear, m0, 15);
  }

  // 7) "Mid Oct a", "Mid Oct b"
  const midRe = new RegExp(`\\bmid\\s+(${MONTH_NAME_PATTERN})\\w*`, 'gi');
  let mid;
  while ((mid = midRe.exec(hay)) !== null) {
    const monKey = mid[1].toLowerCase();
    const m0 = MONTH_TOKEN_TO_INDEX[monKey.slice(0, 4) === 'sept' ? 'sept' : monKey.slice(0, 3)];
    if (m0 == null) continue;
    pushUtc(defaultYear, m0, 15);
  }

  // 8) Lone month name (e.g. title line) — only if preceded by em dash or start-ish
  const loneRe = new RegExp(
    `(?:^|[—\\-]\\s*)(${MONTH_NAME_PATTERN})\\w*\\b(?!\\s+\\d{1,2}\\b)`,
    'gi'
  );
  let lo;
  while ((lo = loneRe.exec(hay)) !== null) {
    const monKey = lo[1].toLowerCase();
    const m0 = MONTH_TOKEN_TO_INDEX[monKey.slice(0, 4) === 'sept' ? 'sept' : monKey.slice(0, 3)];
    if (m0 == null) continue;
    pushUtc(defaultYear, m0, 15);
  }

  if (candidates.length > 0) return Math.max(...candidates);

  // 9) "Month 3" style — subscription period index (no calendar in string)
  const monthOrd = hay.match(/\bmonth\s*(\d+)\b/i);
  if (monthOrd) {
    const n = +monthOrd[1];
    if (n >= 1 && n <= 240) {
      const ORD0 = Date.UTC(1985, 0, 1);
      return ORD0 + (n - 1) * 28 * 86400000;
    }
  }

  // 10) Hetzner-style "Cloud Month 7"
  const cloudMonth = hay.match(/cloud\s+month\s*(\d+)\b/i);
  if (cloudMonth) {
    const n = +cloudMonth[1];
    if (n >= 1 && n <= 240) {
      const ORD0 = Date.UTC(1985, 0, 1);
      return ORD0 + (n - 1) * 28 * 86400000;
    }
  }

  // 11) Upload / link time — keeps "Invoice UPZ…" rows in real chronological order vs dated labels
  const uploaded = parseDocCreatedAtMs(doc);
  if (uploaded != null) return uploaded;

  // Last resort: stable id order (~Jan 2020 + id seconds — same magnitude as real dates, not 1e15)
  return Date.UTC(2020, 0, 1) + (doc.id || 0) * 1000;
}

function sortVendorDocsChronologically(catId, docs) {
  if (!VENDOR_IDS_CHRONO_SORT.has(catId)) return docs;
  return [...docs].sort((a, b) => {
    const ka = invoiceChronologicalSortKeyMs(a);
    const kb = invoiceChronologicalSortKeyMs(b);
    if (ka !== kb) return ka - kb;
    const ca = parseDocCreatedAtMs(a) || 0;
    const cb = parseDocCreatedAtMs(b) || 0;
    if (ca !== cb) return ca - cb;
    const fp = String(a.file_path || '').localeCompare(String(b.file_path || ''));
    if (fp !== 0) return fp;
    return (a.id || 0) - (b.id || 0);
  });
}

function documentsForAgent(docs, agentId) {
  if (!agentId) return [];
  return (docs || []).filter(
    (doc) => doc.agents && doc.agents.some((a) => a.agentId === agentId)
  );
}

/** Native browser tooltip: file path + human-readable linked agent names (no sidebar pills). */
function linkedAgentsTooltip(doc, subagents) {
  const path = doc.file_path || '';
  const agents = doc.agents || [];
  if (agents.length === 0) return path || doc.label || '';
  const names = agents.map((a) => {
    const sa = (subagents || []).find((s) => s.id === a.agentId);
    return sa?.name || String(a.agentId).replace(/_/g, ' ');
  });
  const unique = [...new Set(names)];
  return `${path}\n\nLinked agents:\n${unique.join('\n')}`;
}

/** Short sidebar hints for the "reconciliation" id vs the human sub-agent name */
const LINKED_DOCS_SIDEBAR_HINTS = {
  business_auditor:
    'Documents tagged during Business Auditor compliance intake. Shown when you select Business Auditor above.',
  reconciliation:
    'Inter-Agent Reconciliation (Tier 3): this agent cross-checks records across departments. Documents tagged reconciliation are in scope for those checks.'
};

/**
 * Business tab content: sub-agents list (left), right = details panel + chat when sub-agent selected,
 * or task checklist + compliance panel + coordinator chat when Business Auditor (no sub-agent) selected.
 *
 * pageMode (first-class /opo/auditor): URL-driven agent, slim external nav, Command Center landing,
 * accountant tools in the workspace, chat as a collapsible dock for accountant/generic agents.
 */
const AuditorBusinessPanel = ({
  pageMode = false,
  agentId: controlledAgentId,
  onAgentNavigate,
  coordinatorView,
  onCoordinatorViewChange,
  statusSnapshot = null,
} = {}) => {
  const [subagents, setSubagents] = useState([]);
  const [internalAgentId, setInternalAgentId] = useState(null);
  const selectedSubAgentId = pageMode
    ? (controlledAgentId !== undefined ? controlledAgentId : null)
    : internalAgentId;
  const setSelectedSubAgentId = useCallback(
    (next) => {
      const id = typeof next === 'function' ? next(selectedSubAgentId) : next;
      if (pageMode && typeof onAgentNavigate === 'function') {
        onAgentNavigate(id);
      } else {
        setInternalAgentId(id);
      }
    },
    [pageMode, onAgentNavigate, selectedSubAgentId]
  );
  const [byArea, setByArea] = useState({});
  const [history, setHistory] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingSubagents, setLoadingSubagents] = useState(true);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [query, setQuery] = useState('');
  const [documentSummary, setDocumentSummary] = useState(null);
  const [summaryCreatedAt, setSummaryCreatedAt] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  /** PDF linked docs: server-side extract cache (OCR / text layer) */
  const [linkedExtractStatus, setLinkedExtractStatus] = useState(null);
  const [linkedExtractLoading, setLinkedExtractLoading] = useState(false);
  const [summaryRefreshExtract, setSummaryRefreshExtract] = useState(false);
  const [accountantDocuments, setAccountantDocuments] = useState([]);
  const [accountantDocsLoading, setAccountantDocsLoading] = useState(false);
  const [addingDocument, setAddingDocument] = useState(false);
  /** Active right-pane tab when on the Business Auditor coordinator (no sub-agent selected). */
  const [internalCoordinatorTab, setInternalCoordinatorTab] = useState('compliance');
  const coordinatorTab = pageMode && coordinatorView
    ? coordinatorView
    : internalCoordinatorTab;
  const setCoordinatorTab = useCallback(
    (next) => {
      if (pageMode && typeof onCoordinatorViewChange === 'function') {
        onCoordinatorViewChange(next);
      } else {
        setInternalCoordinatorTab(next);
      }
    },
    [pageMode, onCoordinatorViewChange]
  );
  /** Collapsible Ask dock for accountant / generic agents in pageMode */
  const [chatDockOpen, setChatDockOpen] = useState(false);
  /** Width (px) of the left sub-agents column — drag the right edge to resize. */
  const [subagentsWidth, setSubagentsWidth] = useState(() => {
    if (typeof window === 'undefined') return 380;
    const saved = Number(window.localStorage?.getItem('auditor-subagents-width'));
    return Number.isFinite(saved) && saved >= 260 && saved <= 820 ? saved : 420;
  });
  const subagentsDragRef = useRef(null);
  const panelsRef = useRef(null);
  const subagentsListRef = useRef(null);
  /** vendor category id currently open on the right ('hetzner' | 'porkbun' | … | null) */
  const [selectedVendorCategoryId, setSelectedVendorCategoryId] = useState(null);
  const [selectedAccountantDocId, setSelectedAccountantDocId] = useState(null);
  /** Coordinator: selected linked doc for Business Auditor — linked files (summary panel) */
  const [selectedBusinessAuditorDocId, setSelectedBusinessAuditorDocId] = useState(null);
  // Compliance scan state
  const [complianceScan, setComplianceScan] = useState(null);
  const [complianceScanLoading, setComplianceScanLoading] = useState(false);
  const [compliancePriorityFilter, setCompliancePriorityFilter] = useState('all');
  const [complianceSectionFilter, setComplianceSectionFilter] = useState('all');
  // Compliance item notes: { [item.expectedPath]: { note, updated_at } }
  const [complianceNotes, setComplianceNotes] = useState({});
  /** T2 CORP INFO worksheet (Line 299 NAICS) — Business Auditor coordinator */
  const [corpInfo, setCorpInfo] = useState(null);
  const [corpInfoLoading, setCorpInfoLoading] = useState(false);
  const [corpInfoGenerating, setCorpInfoGenerating] = useState(false);
  // Note modal: null | { item, draft, saving, error }
  const [noteModal, setNoteModal] = useState(null);
  // Generic sub-agent upload state
  const [subAgentUploadLoading, setSubAgentUploadLoading] = useState(false);
  // Compliance document upload modal state
  const [uploadModal, setUploadModal] = useState(null); // null | { item, stages[], qwenText, done, error }
  /** Linked-files panels: expanded only when key is explicitly `true` (default collapsed) */
  const [linkedFilesExpanded, setLinkedFilesExpanded] = useState({});
  const complianceUploadItemRef = useRef(null);
  const fileInputRef = useRef(null);
  const vendorAddInputRef = useRef(null);
  const subAgentFileInputRef = useRef(null);
  const complianceFileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const queryBarRef = useRef(null);

  const selectedSubAgent = selectedSubAgentId
    ? subagents.find((a) => a.id === selectedSubAgentId)
    : null;

  // Reset workspace selection when switching specialists (page or modal)
  useEffect(() => {
    setSelectedVendorCategoryId(null);
    setSelectedAccountantDocId(null);
    setSelectedBusinessAuditorDocId(null);
    setChatDockOpen(false);
    if (pageMode && selectedSubAgentId === 'accountant') {
      setLinkedFilesExpanded((prev) => ({ ...prev, accountant: true }));
    }
  }, [selectedSubAgentId, pageMode]);

  // Collapse Ask dock when a document/category is open (accountant)
  useEffect(() => {
    if (selectedAccountantDocId != null || selectedVendorCategoryId != null) {
      setChatDockOpen(false);
    }
  }, [selectedAccountantDocId, selectedVendorCategoryId]);

  const companyCostRegisterDoc = (accountantDocuments || []).find(
    (d) => d.file_path === ACCOUNTANT_COMPANY_REGISTER_PATH
  );
  /**
   * Map from vendor category id → matching docs.
   * Matches against ALL accountantDocuments (not just agent-tagged ones) so newly
   * uploaded invoices appear immediately regardless of backfill status.
   * '_other' key collects docs that don't match any defined category.
   */
  const vendorDocsByCategory = React.useMemo(() => {
    const base = (accountantDocuments || []).filter(
      (d) =>
        d.file_path !== ACCOUNTANT_COMPANY_REGISTER_PATH &&
        !/^expenses\/bank_statements\//.test(d.file_path)
    );
    const categorizedIds = new Set();
    const result = {};
    for (const cat of VENDOR_CATEGORIES) {
      const docs = base.filter((d) => cat.match(d));
      result[cat.id] = sortVendorDocsChronologically(cat.id, docs);
      result[cat.id].forEach((d) => categorizedIds.add(d.id));
    }
    result['_other'] = base.filter((d) => !categorizedIds.has(d.id));
    return result;
  }, [accountantDocuments]);

  const fetchSubagents = () => {
    fetch('/api/opo/auditor/subagents')
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to load subagents')))
      .then((d) => setSubagents(d.subagents || []))
      .catch((e) => console.error('Subagents:', e))
      .finally(() => setLoadingSubagents(false));
  };

  const fetchAccountantDocuments = () => {
    setAccountantDocsLoading(true);
    fetch('/api/opo/auditor/subagent/accountant/documents')
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to load documents')))
      .then((d) => setAccountantDocuments(d.documents || []))
      .catch((e) => console.error('Accountant documents:', e))
      .finally(() => setAccountantDocsLoading(false));
  };

  const fetchComplianceScan = useCallback(() => {
    setComplianceScanLoading(true);
    fetch('/api/opo/auditor/compliance-scan')
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to load compliance scan')))
      .then((d) => setComplianceScan(d))
      .catch((e) => console.error('Compliance scan:', e))
      .finally(() => setComplianceScanLoading(false));
  }, []);

  const fetchComplianceNotes = useCallback(() => {
    fetch('/api/opo/auditor/compliance-notes')
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to load notes')))
      .then((d) => setComplianceNotes(d.notes || {}))
      .catch((e) => console.error('Compliance notes:', e));
  }, []);

  const fetchCorpInfo = useCallback(() => {
    setCorpInfoLoading(true);
    fetch('/api/opo/t2/corp-info')
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to load CORP INFO')))
      .then((d) => setCorpInfo(d))
      .catch((e) => console.error('CORP INFO:', e))
      .finally(() => setCorpInfoLoading(false));
  }, []);

  const generateCorpInfoPdf = useCallback(() => {
    setCorpInfoGenerating(true);
    fetch('/api/opo/t2/corp-info/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ force: true }),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Generate failed')))
      .then((d) => setCorpInfo(d))
      .catch((e) => console.error('CORP INFO generate:', e))
      .finally(() => setCorpInfoGenerating(false));
  }, []);

  const saveComplianceNote = useCallback((item_key, note) => {
    setNoteModal((m) => m ? { ...m, saving: true, error: null } : m);
    fetch('/api/opo/auditor/compliance-notes', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_key, note }),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Save failed')))
      .then((d) => {
        setComplianceNotes((prev) => {
          const next = { ...prev };
          if (d.note) {
            next[item_key] = { note: d.note, updated_at: new Date().toISOString() };
          } else {
            delete next[item_key];
          }
          return next;
        });
        setNoteModal(null);
      })
      .catch((e) => {
        setNoteModal((m) => m ? { ...m, saving: false, error: e.message } : m);
      });
  }, []);

  const fetchTasks = () => {
    fetch('/api/opo/auditor/tasks')
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to load tasks')))
      .then((d) => setByArea(d.byArea || {}))
      .catch((e) => console.error('Auditor tasks:', e))
      .finally(() => setLoadingTasks(false));
  };

  const fetchHistory = () => {
    const url = selectedSubAgentId
      ? `/api/opo/auditor/history?subAgent=${encodeURIComponent(selectedSubAgentId)}`
      : '/api/opo/auditor/history';
    setLoadingHistory(true);
    fetch(url)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to load history')))
      .then((d) => setHistory(d.history || []))
      .catch((e) => console.error('History:', e))
      .finally(() => setLoadingHistory(false));
  };

  useEffect(() => {
    fetchSubagents();
    fetchAccountantDocuments();
    fetchComplianceScan();
    fetchComplianceNotes();
    fetchCorpInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchComplianceScan, fetchComplianceNotes, fetchCorpInfo]);

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [selectedSubAgentId]);

  // Keep the active sub-agent button in view (e.g. coordinator at bottom after tier-first layout).
  useEffect(() => {
    if (loadingSubagents) return;
    const root = subagentsListRef.current;
    if (!root) return;
    const active = root.querySelector('.auditor-subagent-item-active');
    active?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }, [selectedSubAgentId, loadingSubagents]);

  const activeLinkedSummaryDocId =
    selectedSubAgentId === 'accountant'
      ? selectedAccountantDocId
      : selectedSubAgentId == null
        ? selectedBusinessAuditorDocId
        : null;

  const selectedLinkedDocForSummary = (accountantDocuments || []).find((d) => d.id === activeLinkedSummaryDocId);
  const isActiveLinkedDocPdf = !!(
    selectedLinkedDocForSummary &&
    /\.pdf$/i.test(String(selectedLinkedDocForSummary.file_path || ''))
  );

  // Clear summary error whenever the active doc changes
  useEffect(() => {
    setSummaryError(null);
    setSummaryRefreshExtract(false);
    setDocumentSummary(null);
    setSummaryCreatedAt(null);
  }, [activeLinkedSummaryDocId]);

  // Fetch server-side extract status for PDF docs.
  // Depends on accountantDocuments so it re-runs once docs finish loading.
  useEffect(() => {
    if (!activeLinkedSummaryDocId) {
      setLinkedExtractStatus(null);
      return;
    }
    if (!isActiveLinkedDocPdf) {
      setLinkedExtractStatus(null);
      return;
    }
    setLinkedExtractLoading(true);
    fetch(`/api/opo/auditor/linked-documents/${activeLinkedSummaryDocId}/extract-status`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('extract-status failed'))))
      .then((d) => setLinkedExtractStatus(d))
      .catch(() => setLinkedExtractStatus(null))
      .finally(() => setLinkedExtractLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLinkedSummaryDocId, isActiveLinkedDocPdf, accountantDocuments]);

  const fetchLinkedDocumentSummary = useCallback(() => {
    if (!activeLinkedSummaryDocId) {
      setDocumentSummary(null);
      setSummaryCreatedAt(null);
      return;
    }
    const id = activeLinkedSummaryDocId;
    const isCompanyCostRegister =
      selectedSubAgentId === 'accountant' &&
      (accountantDocuments || []).some(
        (d) => d.id === id && d.file_path === 'expenses/COMPANY_COST_REGISTER.xlsx'
      );

    fetch(`/api/opo/auditor/linked-documents/${id}/summary`)
      .then((r) => {
        if (r.ok) return r.json();
        // Older deployments may not expose this route yet — fall back to legacy accountant summary API
        if (r.status === 404 && isCompanyCostRegister) {
          return fetch('/api/opo/auditor/subagent/accountant/summary').then((r2) => {
            if (!r2.ok) throw new Error('Legacy summary unavailable');
            return r2.json();
          });
        }
        throw new Error('Summary fetch failed');
      })
      .then((d) => {
        setDocumentSummary(d.summary || null);
        setSummaryCreatedAt(d.createdAt || null);
      })
      .catch(() => {
        setDocumentSummary(null);
        setSummaryCreatedAt(null);
      });
  }, [activeLinkedSummaryDocId, selectedSubAgentId, accountantDocuments]);

  useEffect(() => {
    fetchLinkedDocumentSummary();
  }, [fetchLinkedDocumentSummary]);

  useEffect(() => {
    if (selectedSubAgentId) setSelectedBusinessAuditorDocId(null);
  }, [selectedSubAgentId]);

  useEffect(() => {
    if (selectedSubAgentId !== 'accountant') {
      setSelectedAccountantDocId(null);
      setSelectedVendorCategoryId(null);
    }
    if (selectedSubAgentId === T2_SUBAGENT_ID) {
      setLinkedFilesExpanded((prev) => ({
        ...prev,
        [`subagent_${T2_SUBAGENT_ID}`]: true,
      }));
    }
    if (selectedSubAgentId === AGM_SUBAGENT_ID) {
      setLinkedFilesExpanded((prev) => ({
        ...prev,
        [`subagent_${AGM_SUBAGENT_ID}`]: true,
      }));
    }
  }, [selectedSubAgentId]);

  // Sub-agents column horizontal drag-to-resize: global listeners
  useEffect(() => {
    const onMove = (e) => {
      if (!subagentsDragRef.current) return;
      const { startX, startWidth } = subagentsDragRef.current;
      const dx = e.clientX - startX;
      const containerW = panelsRef.current ? panelsRef.current.offsetWidth : 1200;
      // Reserve at least 360px for the right pane so the chat / tabs don't get squashed.
      const maxWidth = Math.min(820, Math.max(280, containerW - 400));
      const next = Math.max(260, Math.min(maxWidth, startWidth + dx));
      setSubagentsWidth(next);
    };
    const onUp = () => {
      if (!subagentsDragRef.current) return;
      subagentsDragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        window.localStorage?.setItem('auditor-subagents-width', String(subagentsWidth));
      } catch (_) {}
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [subagentsWidth]);

  const onSubagentsResizeDown = (e) => {
    e.preventDefault();
    subagentsDragRef.current = { startX: e.clientX, startWidth: subagentsWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleGenerateSummary = () => {
    if (!activeLinkedSummaryDocId || summaryLoading) return;
    const id = activeLinkedSummaryDocId;
    const context = selectedSubAgentId === 'accountant' ? 'accountant' : 'business_auditor';
    const isCompanyCostRegister =
      selectedSubAgentId === 'accountant' &&
      (accountantDocuments || []).some(
        (d) => d.id === id && d.file_path === 'expenses/COMPANY_COST_REGISTER.xlsx'
      );
    const refreshExtract = summaryRefreshExtract;

    setSummaryLoading(true);
    setSummaryError(null);
    fetch(`/api/opo/auditor/linked-documents/${id}/summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ context, refreshExtract }),
    })
      .then((r) => {
        if (r.ok) return r.json();
        if (r.status === 404 && isCompanyCostRegister) {
          return fetch('/api/opo/auditor/subagent/accountant/summary', { method: 'POST' }).then((r2) => {
            if (!r2.ok) throw new Error('Failed to generate (legacy)');
            return r2.json();
          });
        }
        return r.json().then((body) => Promise.reject(new Error((body && body.error) || 'Failed to generate')));
      })
      .then((d) => {
        setDocumentSummary(d.summary || null);
        setSummaryCreatedAt(d.createdAt || null);
        if (isActiveLinkedDocPdf && activeLinkedSummaryDocId) {
          fetch(`/api/opo/auditor/linked-documents/${activeLinkedSummaryDocId}/extract-status`)
            .then((r) => (r.ok ? r.json() : null))
            .then((st) => st && setLinkedExtractStatus(st))
            .catch(() => {});
        }
      })
      .catch((e) => {
        console.error('Generate summary:', e);
        setSummaryError(e && e.message ? e.message : 'Failed to generate summary');
      })
      .finally(() => setSummaryLoading(false));
  };

  const showLinkedDocSummaryPanel =
    (selectedSubAgentId === 'accountant' &&
      (selectedAccountantDocId != null || selectedVendorCategoryId != null)) ||
    (selectedSubAgentId == null && selectedBusinessAuditorDocId != null);

  const showAccountantInstructions =
    selectedSubAgentId === 'accountant' &&
    !selectedAccountantDocId &&
    !selectedVendorCategoryId;

  // Scroll chat to bottom when messages change, when switching back from document summary,
  // or when the coordinator switches to the Conversation tab.
  useEffect(() => {
    if (showLinkedDocSummaryPanel) return; // summary visible, chat hidden
    if (!selectedSubAgentId && coordinatorTab !== 'chat') return; // coordinator + chat tab not active
    const t = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(t);
  }, [history, streamingMessage, showLinkedDocSummaryPanel, selectedSubAgentId, coordinatorTab]);

  const onAddDocumentClick = () => fileInputRef.current?.click();

  const onVendorAddFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const selectedVendor = VENDOR_CATEGORIES.find((c) => c.id === selectedVendorCategoryId);
    const baseLabel = file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ').trim();
    const label = selectedVendor ? `${selectedVendor.label} - ${baseLabel}` : baseLabel;
    setAddingDocument(true);
    setSummaryError(null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('label', label);
    if (selectedVendorCategoryId) formData.append('categoryHint', selectedVendorCategoryId);
    fetch('/api/opo/auditor/subagent/accountant/documents', { method: 'POST', body: formData })
      .then((r) => r.ok ? r.json() : r.json().then((d) => Promise.reject(new Error(d.error || 'Upload failed'))))
      .then((d) => {
        setAccountantDocuments(d.documents || []);
        const newId = d.linkedDocumentId;
        if (!newId) return null;
        setSelectedAccountantDocId(newId);
        setSummaryLoading(true);
        return fetch(`/api/opo/auditor/linked-documents/${newId}/extract-status`)
          .then((r) => (r.ok ? r.json() : null))
          .then((st) => st && setLinkedExtractStatus(st))
          .catch(() => {})
          .then(() =>
            fetch(`/api/opo/auditor/linked-documents/${newId}/summary`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ context: 'accountant', refreshExtract: true }),
            })
          )
          .then((r) =>
            r.ok ? r.json() : r.json().then((body) => Promise.reject(new Error((body && body.error) || 'Summary failed')))
          )
          .then((sum) => {
            setDocumentSummary(sum.summary || null);
            setSummaryCreatedAt(sum.createdAt || null);
          })
          .finally(() => setSummaryLoading(false));
      })
      .catch((err) => {
        console.error('Vendor add document:', err);
        setSummaryError(err.message || 'Upload failed');
      })
      .finally(() => { setAddingDocument(false); e.target.value = ''; });
  };

  const onDocumentFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setAddingDocument(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('label', file.name.replace(/\.[^.]+$/, '').replace(/_/g, ' '));
    fetch('/api/opo/auditor/subagent/accountant/documents', { method: 'POST', body: formData })
      .then((r) => r.ok ? r.json() : r.json().then((d) => Promise.reject(new Error(d.error || 'Upload failed'))))
      .then((d) => {
        setAccountantDocuments(d.documents || []);
      })
      .catch((err) => console.error('Add document:', err))
      .finally(() => {
        setAddingDocument(false);
        e.target.value = '';
      });
  };

  const onSubAgentFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !selectedSubAgentId || selectedSubAgentId === 'accountant') return;
    setSubAgentUploadLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('label', file.name.replace(/\.[^.]+$/, '').replace(/_/g, ' '));
    fetch(`/api/opo/auditor/subagent/${selectedSubAgentId}/documents`, { method: 'POST', body: formData })
      .then((r) => r.ok ? r.json() : r.json().then((d) => Promise.reject(new Error(d.error || 'Upload failed'))))
      .then((d) => {
        if (d.scan) setComplianceScan(d.scan);
        else fetchComplianceScan();
      })
      .catch((err) => console.error('Sub-agent upload:', err))
      .finally(() => {
        setSubAgentUploadLoading(false);
        e.target.value = '';
      });
  };

  const onComplianceUploadClick = (item) => {
    complianceUploadItemRef.current = { item, replace: false };
    complianceFileInputRef.current?.click();
  };

  const onComplianceEditClick = (item) => {
    complianceUploadItemRef.current = { item, replace: true };
    complianceFileInputRef.current?.click();
  };

  const onComplianceFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    const ref = complianceUploadItemRef.current;
    const item = ref && (ref.item || ref);
    const isReplace = ref && ref.replace === true;
    if (!file || !item) {
      e.target.value = '';
      complianceUploadItemRef.current = null;
      return;
    }
    complianceUploadItemRef.current = null;
    e.target.value = '';

    // Open modal immediately
    setUploadModal({ item, stages: [], qwenText: '', done: false, error: null });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('expectedPath', item.expectedPath);
    formData.append('document', item.document);
    formData.append('owner', item.owner);
    formData.append('description', item.description || '');
    if (isReplace) formData.append('replace', 'true');

    fetch('/api/opo/auditor/compliance-document/upload-verify', { method: 'POST', body: formData })
      .then((r) => {
        if (!r.ok || !r.body) throw new Error('Upload request failed');
        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        const pump = () => reader.read().then(({ value, done: streamDone }) => {
          if (value) buf += decoder.decode(value, { stream: true });
          // Parse SSE lines
          let idx;
          while ((idx = buf.indexOf('\n\n')) !== -1) {
            const block = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            const dataLine = block.split('\n').find(l => l.startsWith('data: '));
            if (!dataLine) continue;
            try {
              const evt = JSON.parse(dataLine.slice(6));
              setUploadModal((prev) => {
                if (!prev) return prev;
                if (evt.stage === 'qwen_token') {
                  return { ...prev, qwenText: (prev.qwenText || '') + evt.message };
                }
                if (evt.stage === 'done') {
                  if (evt.data && evt.data.scan) setComplianceScan(evt.data.scan);
                  else fetchComplianceScan();
                  fetchAccountantDocuments();
                  return { ...prev, stages: [...prev.stages, { stage: evt.stage, message: evt.message }], done: true };
                }
                if (evt.stage === 'error') {
                  return { ...prev, stages: [...prev.stages, { stage: evt.stage, message: evt.message }], done: true, error: evt.message };
                }
                return { ...prev, stages: [...prev.stages, { stage: evt.stage, message: evt.message }] };
              });
            } catch (_) {}
          }
          if (!streamDone) return pump();
        });
        return pump();
      })
      .catch((err) => {
        console.error('Compliance upload stream error:', err);
        setUploadModal((prev) => prev ? { ...prev, done: true, error: err.message } : prev);
      });
  };

  const updateTaskStatus = (taskId, currentStatus) => {
    const next = { pending: 'in_progress', in_progress: 'complete', complete: 'pending', needs_attention: 'in_progress' }[currentStatus] || 'pending';
    fetch(`/api/opo/auditor/tasks/${taskId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: next })
    })
      .then((r) => r.ok ? fetchTasks() : Promise.reject())
      .catch((e) => console.error('Update task:', e));
  };

  const sendMessage = async () => {
    const msg = query.trim();
    if (!msg || streaming) return;
    setQuery('');
    setStreaming(true);
    setStreamingMessage('');
    setHistory((prev) => [...prev, { role: 'user', content: msg, created_at: new Date().toISOString() }]);

    try {
      const body = selectedSubAgentId
        ? { message: msg, subAgent: selectedSubAgentId }
        : { message: msg };
      const res = await fetch('/api/opo/auditor/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok || !res.body) {
        setStreamingMessage('Error: ' + (res.statusText || 'No response body'));
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        full += chunk;
        setStreamingMessage(full);
      }
      setHistory((prev) => [...prev, { role: 'assistant', content: full, created_at: new Date().toISOString() }]);
      setStreamingMessage('');
      if (!selectedSubAgentId) { fetchTasks(); fetchComplianceScan(); }
      fetchLinkedDocumentSummary();
    } catch (e) {
      setStreamingMessage('Error: ' + (e.message || 'Request failed'));
    } finally {
      setStreaming(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const areas = Object.keys(byArea).sort();
  const isCoordinator = !selectedSubAgentId;

  const toggleLinkedFilesSection = (key) => {
    setLinkedFilesExpanded((prev) => ({
      ...prev,
      [key]: prev[key] !== true,
    }));
  };
  const isLinkedExpanded = (key) => linkedFilesExpanded[key] === true;

  /** Read-only linked doc tiles (Business Auditor, Tax, Reconciliation, etc.) */
  const readOnlyLinkedDocsSection = (agentFilterId, title, sectionKey = agentFilterId, linkOpts = {}) => {
    const { selectable = false, selectedDocId = null, onToggleDocId } = linkOpts;
    const hint = LINKED_DOCS_SIDEBAR_HINTS[agentFilterId] || null;
    const list = documentsForAgent(accountantDocuments, agentFilterId);
    const expanded = isLinkedExpanded(sectionKey);
    return (
      <>
        <div className="auditor-sidebar-sep" />
        <div className="auditor-sidebar-docs auditor-sidebar-accountant-linked">
          <div className="auditor-linked-docs-header">
            <button
              type="button"
              className="auditor-linked-docs-toggle"
              onClick={() => toggleLinkedFilesSection(sectionKey)}
              aria-expanded={expanded}
              title={expanded ? 'Hide linked files' : 'Show linked files'}
            >
              <span className={`auditor-linked-docs-chevron ${!expanded ? 'is-collapsed' : ''}`} aria-hidden>
                ▼
              </span>
            </button>
            <div className="auditor-linked-docs-header-titles">
              <div className="auditor-sidebar-docs-title">{title}</div>
            </div>
          </div>
          {expanded && (
            <>
              {hint ? <p className="auditor-sidebar-accountant-hint">{hint}</p> : null}
              {accountantDocsLoading ? (
                <div className="auditor-sidebar-docs-loading">Loading…</div>
              ) : list.length === 0 ? (
                <p className="auditor-sidebar-accountant-hint">
                  No documents tagged <code className="auditor-inline-code">{agentFilterId}</code> yet.
                </p>
              ) : (
                <div className="auditor-sidebar-docs-grid">
                  {list.map((doc) => {
                    const tooltip = linkedAgentsTooltip(doc, subagents);
                    const hasAgents = doc.agents && doc.agents.length > 0;
                    return (
                    <div
                      key={doc.id}
                      className={`auditor-doc-tile-wrap${hasAgents ? ' auditor-doc-tile-wrap--has-agents' : ''}`}
                      title={tooltip}
                    >
                      {selectable && typeof onToggleDocId === 'function' ? (
                        <button
                          type="button"
                          className={`auditor-doc-tile auditor-doc-tile-readonly${selectedDocId === doc.id ? ' auditor-doc-tile-selected' : ''}`}
                          onClick={() => onToggleDocId(doc.id)}
                        >
                          {doc.label}
                        </button>
                      ) : (
                        <div className="auditor-doc-tile auditor-doc-tile-readonly">
                          {doc.label}
                        </div>
                      )}
                      <a
                        href={`/opo/document/${doc.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="auditor-doc-view-link"
                        title="Open document in new tab"
                        onClick={(e) => openDocumentViewerTab(doc.id, e)}
                      >
                        View
                      </a>
                    </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </>
    );
  };

  const businessAuditorLinkedDocsSidebar = isCoordinator
    ? readOnlyLinkedDocsSection('business_auditor', 'Linked files', 'business_auditor', {
        selectable: true,
        selectedDocId: selectedBusinessAuditorDocId,
        onToggleDocId: (id) =>
          setSelectedBusinessAuditorDocId((prev) => (prev === id ? null : id)),
      })
    : null;

  /** Upload + linked files nested under the active specialist (not at the bottom of the sidebar). */
  const renderGenericSubAgentTools = (agentId) => {
    if (selectedSubAgentId !== agentId || agentId === 'accountant') return null;
    const agent = (subagents || []).find((a) => a.id === agentId);
    const agentLabel = agent?.name || agentId;
    const uploadDir = agent?.uploadTargetDir || agentId;
    return (
      <div className="auditor-sidebar-agent-tools">
        <div className="auditor-sidebar-docs">
          <div className="auditor-sidebar-docs-title">Documents for {agentLabel}</div>
          <button
            type="button"
            className="auditor-doc-add"
            onClick={() => subAgentFileInputRef.current?.click()}
            disabled={subAgentUploadLoading}
          >
            {subAgentUploadLoading ? 'Uploading…' : '+ Upload document'}
          </button>
          <input
            ref={subAgentFileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.md,.txt,.png,.jpg,.jpeg"
            className="auditor-doc-input-hidden"
            onChange={onSubAgentFileChange}
          />
          <p className="auditor-sidebar-accountant-hint">
            Saved to <code className="auditor-inline-code">01_auditor/{uploadDir}/</code>
          </p>
        </div>
        {readOnlyLinkedDocsSection(agentId, 'Linked files', `subagent_${agentId}`)}
      </div>
    );
  };

  const renderSubAgentButton = (a, { featured = false } = {}) => (
    <button
      type="button"
      className={`auditor-subagent-item${featured ? ' auditor-subagent-item--featured' : ''}${a.t2 ? ' auditor-subagent-item--t2' : ''}${a.agm ? ' auditor-subagent-item--agm' : ''}${selectedSubAgentId === a.id ? ' auditor-subagent-item-active' : ''}`}
      onClick={() => setSelectedSubAgentId(a.id)}
    >
      <span className="auditor-subagent-item-label">{a.name}</span>
      {a.t2 ? (
        <span className="auditor-subagent-item-meta">T2 return · SR&ED · BC</span>
      ) : a.agm ? (
        <span className="auditor-subagent-item-meta">First AGM · 2026-06-04</span>
      ) : a.description ? (
        <span className="auditor-subagent-item-meta">
          {(a.description.split('.')[0] || a.description).slice(0, 52)}
          {(a.description.split('.')[0] || a.description).length > 52 ? '…' : ''}
        </span>
      ) : null}
    </button>
  );

  /** Linked documents + upload — shown directly under Accountant in the sidebar (not below all tiers). */
  const accountantLinkedDocsSidebar =
    selectedSubAgentId === 'accountant' ? (
      <>
        <div className="auditor-sidebar-sep" />
        <div className="auditor-sidebar-docs auditor-sidebar-accountant-linked">
          <div className="auditor-linked-docs-header">
            <button
              type="button"
              className="auditor-linked-docs-toggle"
              onClick={() => toggleLinkedFilesSection('accountant')}
              aria-expanded={isLinkedExpanded('accountant')}
              title={isLinkedExpanded('accountant') ? 'Hide linked files' : 'Show linked files'}
            >
              <span className={`auditor-linked-docs-chevron ${!isLinkedExpanded('accountant') ? 'is-collapsed' : ''}`} aria-hidden>
                ▼
              </span>
            </button>
            <div className="auditor-linked-docs-header-titles">
              <div className="auditor-sidebar-docs-title">Accountant documents</div>
            </div>
          </div>
          {isLinkedExpanded('accountant') &&
            (accountantDocsLoading ? (
              <div className="auditor-sidebar-docs-loading">Loading…</div>
            ) : (
              <>
                <div className="auditor-sidebar-docs-grid">
                {documentsForAgent(accountantDocuments, 'accountant').length === 0 &&
                !companyCostRegisterDoc &&
                !accountantDocsLoading ? (
                  <p className="auditor-sidebar-accountant-hint">
                    No documents tagged <code className="auditor-inline-code">accountant</code> yet. Use <strong>+ Add document</strong> or compliance uploads owned by Accountant.
                  </p>
                ) : null}
                <div className="auditor-accountant-canonical-row">
                  {companyCostRegisterDoc ? (
                    <div className="auditor-doc-tile-wrap">
                      <button
                        type="button"
                        className={`auditor-doc-tile${selectedAccountantDocId === companyCostRegisterDoc.id ? ' auditor-doc-tile-selected' : ''}`}
                        title={companyCostRegisterDoc.file_path}
                        onClick={() => {
                          setSelectedAccountantDocId((prev) =>
                            prev === companyCostRegisterDoc.id ? null : companyCostRegisterDoc.id
                          );
                        }}
                      >
                        {companyCostRegisterDoc.label}
                      </button>
                      <a
                        href={`/opo/document/${companyCostRegisterDoc.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="auditor-doc-view-link"
                        title="Open ASPE preparation record (bank statements, ASPE tabs)"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDocumentViewerTab(companyCostRegisterDoc.id, e);
                        }}
                      >
                        View
                      </a>
                    </div>
                  ) : null}
                </div>
                {/* Vendor category tiles */}
                <div className="auditor-vendor-cats-grid">
                  {VENDOR_CATEGORIES.map((cat) => {
                    const catCount = (vendorDocsByCategory[cat.id] || []).length;
                    const isActive = selectedVendorCategoryId === cat.id;
                    return (
                      <div key={cat.id} className="auditor-doc-tile-wrap">
                        <button
                          type="button"
                          className={`auditor-doc-tile auditor-vendor-cat-tile${isActive ? ' auditor-doc-tile-selected' : ''}`}
                          onClick={() => {
                            setSelectedAccountantDocId(null);
                            setSelectedVendorCategoryId(isActive ? null : cat.id);
                          }}
                        >
                          {cat.label}
                          {catCount > 0 && (
                            <span className="auditor-vendor-cat-count">{catCount}</span>
                          )}
                        </button>
                      </div>
                    );
                  })}
                  {(vendorDocsByCategory['_other'] || []).length > 0 && (
                    <div className="auditor-doc-tile-wrap">
                      <button
                        type="button"
                        className={`auditor-doc-tile auditor-vendor-cat-tile${selectedVendorCategoryId === '_other' ? ' auditor-doc-tile-selected' : ''}`}
                        onClick={() => {
                          setSelectedAccountantDocId(null);
                          setSelectedVendorCategoryId(selectedVendorCategoryId === '_other' ? null : '_other');
                        }}
                      >
                        OTHER
                        <span className="auditor-vendor-cat-count">{vendorDocsByCategory['_other'].length}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="auditor-doc-add"
                onClick={onAddDocumentClick}
                disabled={addingDocument}
              >
                {addingDocument ? 'Adding…' : '+ Add document'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.pdf,.doc,.docx"
                className="auditor-doc-input-hidden"
                onChange={onDocumentFileChange}
              />
              <div className="auditor-sidebar-accountant-meta">
                <div className="auditor-sidebar-accountant-title">Accountant</div>
                <p className="auditor-sidebar-accountant-hint">
                  Open <strong>ASPE Compliant Preparation Record</strong> for bank statements and financials.
                </p>
                <p className="auditor-sidebar-accountant-upload">
                  Vendor receipts: category tiles above or <strong>+ Add document</strong>.
                </p>
                <div className="auditor-sidebar-accountant-guardrails">
                  <span className="auditor-sidebar-accountant-guardrails-label">Guardrails & instructions</span>
                  <span className="auditor-sidebar-accountant-guardrails-text">Editable in a later phase.</span>
                </div>
              </div>
              </>
            ))}
        </div>
      </>
    ) : null;

  return (
    <div className={`auditor-modal-panels${pageMode ? ' auditor-modal-panels--page' : ''}`} ref={panelsRef}>
      {!pageMode && (
      <>
      <div
        className="auditor-task-panel auditor-subagents-panel"
        style={{ width: `${subagentsWidth}px`, flex: '0 0 auto' }}
      >
        <h3>Navigation</h3>
        {loadingSubagents ? (
          <div className="auditor-task-list">Loading…</div>
        ) : (
          <div className="auditor-subagents-list" ref={subagentsListRef}>
            {/* 1 — Coordinator first: compliance, tasks, company-wide chat */}
            <div className="auditor-nav-section auditor-nav-section--coordinator">
              <div className="auditor-nav-section-label">Overview</div>
              <button
                type="button"
                className={`auditor-subagent-item auditor-subagent-item--coordinator${isCoordinator ? ' auditor-subagent-item-active' : ''}`}
                onClick={() => setSelectedSubAgentId(null)}
              >
                <span className="auditor-subagent-item-label">Business Auditor</span>
                <span className="auditor-subagent-item-meta">Compliance · tasks · intake</span>
              </button>
              {businessAuditorLinkedDocsSidebar}
            </div>

            {/* 1b — Help: deterministic Q&A grounded in the Auditor records */}
            {(() => {
              const helpAgent = (subagents || []).find((a) => a.id === HELP_SUBAGENT_ID || a.help);
              if (!helpAgent) return null;
              return (
                <div className="auditor-nav-section auditor-nav-section--help">
                  <div className="auditor-nav-section-label">Ask</div>
                  {renderSubAgentButton(helpAgent, { featured: true })}
                </div>
              );
            })()}

            {/* 2 — AGM: first annual general meeting */}
            {(() => {
              const agmAgent = (subagents || []).find((a) => a.id === AGM_SUBAGENT_ID || a.agm);
              if (!agmAgent) return null;
              return (
                <div className="auditor-nav-section auditor-nav-section--agm">
                  <div className="auditor-nav-section-label">Governance</div>
                  {renderSubAgentButton(agmAgent, { featured: true })}
                  {renderGenericSubAgentTools(agmAgent.id)}
                </div>
              );
            })()}

            {/* 3 — OptiPrime T2: primary tax workflow (not buried in Tier 1) */}
            {(() => {
              const t2Agent = (subagents || []).find((a) => a.id === T2_SUBAGENT_ID || a.t2);
              if (!t2Agent) return null;
              return (
                <div className="auditor-nav-section auditor-nav-section--t2">
                  <div className="auditor-nav-section-label">Corporate tax return</div>
                  {renderSubAgentButton(t2Agent, { featured: true })}
                  {renderGenericSubAgentTools(t2Agent.id)}
                </div>
              );
            })()}

            {/* 4 — GST & PST: sales tax sub-auditor */}
            {(() => {
              const gstPstAgent = (subagents || []).find((a) => a.id === GST_PST_SUBAGENT_ID || a.gstPst);
              if (!gstPstAgent) return null;
              return (
                <div className="auditor-nav-section auditor-nav-section--gst-pst">
                  <div className="auditor-nav-section-label">Sales tax (GST &amp; PST)</div>
                  {renderSubAgentButton(gstPstAgent, { featured: true })}
                  {renderGenericSubAgentTools(gstPstAgent.id)}
                </div>
              );
            })()}

            {/* 5 — Finance (Tier 1 minus T2, AGM & GST/PST) */}
            {(() => {
              const financeAgents = (subagents || []).filter(
                (a) => a.tier === 1 && a.id !== T2_SUBAGENT_ID && !a.t2 && a.id !== AGM_SUBAGENT_ID && !a.agm && a.id !== GST_PST_SUBAGENT_ID && !a.gstPst && a.id !== HELP_SUBAGENT_ID && !a.help
              );
              if (financeAgents.length === 0) return null;
              return (
                <div className="auditor-nav-section">
                  <div className="auditor-nav-section-label">Finance &amp; records</div>
                  {financeAgents.map((a) => (
                    <React.Fragment key={a.id}>
                      {renderSubAgentButton(a)}
                      {a.id === 'accountant' ? accountantLinkedDocsSidebar : renderGenericSubAgentTools(a.id)}
                    </React.Fragment>
                  ))}
                </div>
              );
            })()}

            {/* 4 — Tier 2 & 3 specialists */}
            {[2, 3].map((tier) => {
              const tierAgents = (subagents || []).filter((a) => a.tier === tier);
              if (tierAgents.length === 0) return null;
              const tierLabels = { 2: 'Governance', 3: 'Internal' };
              return (
                <div key={tier} className="auditor-nav-section auditor-subagents-tier-group">
                  <div className="auditor-nav-section-label">{tierLabels[tier]}</div>
                  {tierAgents.map((a) => (
                    <React.Fragment key={a.id}>
                      {renderSubAgentButton(a)}
                      {renderGenericSubAgentTools(a.id)}
                    </React.Fragment>
                  ))}
                </div>
              );
            })}

            {/* Legacy: sub-agents without a tier */}
            {(subagents || []).filter((a) => !a.tier && a.id !== T2_SUBAGENT_ID && !a.t2 && a.id !== AGM_SUBAGENT_ID && !a.agm && a.id !== GST_PST_SUBAGENT_ID && !a.gstPst && a.id !== HELP_SUBAGENT_ID && !a.help).length > 0 && (
              <div className="auditor-nav-section">
                <div className="auditor-nav-section-label">Other</div>
                {(subagents || [])
                  .filter((a) => !a.tier && a.id !== T2_SUBAGENT_ID && !a.t2 && a.id !== AGM_SUBAGENT_ID && !a.agm && a.id !== GST_PST_SUBAGENT_ID && !a.gstPst && a.id !== HELP_SUBAGENT_ID && !a.help)
                  .map((a) => (
                    <React.Fragment key={a.id}>
                      {renderSubAgentButton(a)}
                      {a.id === 'accountant' ? accountantLinkedDocsSidebar : renderGenericSubAgentTools(a.id)}
                    </React.Fragment>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className="auditor-subagents-resize-handle"
        onMouseDown={onSubagentsResizeDown}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sub-agents column"
        title="Drag to resize"
      />
      </>
      )}
      <div className="auditor-chat-panel">
        {/* Page mode: accountant documents live in the workspace, not the nav */}
        {pageMode && selectedSubAgentId === 'accountant' && !showLinkedDocSummaryPanel && (
          <div className="auditor-workspace-accountant-tools">
            {accountantLinkedDocsSidebar}
          </div>
        )}
        {/* Page mode: upload/linked tools for generic specialists.
            Corporate Records has its own Resolutions workspace and does not need the
            generic linked-file panel. */}
        {pageMode && selectedSubAgentId && selectedSubAgent
          && !selectedSubAgent.t2 && !selectedSubAgent.agm && !selectedSubAgent.gstPst && !selectedSubAgent.help
          && selectedSubAgentId !== 'accountant'
          && selectedSubAgentId !== 'corporate_records'
          && !showLinkedDocSummaryPanel && (
          <div className="auditor-workspace-agent-tools">
            {renderGenericSubAgentTools(selectedSubAgentId)}
          </div>
        )}
        {/* Page mode: Business Auditor linked files on Command Center */}
        {pageMode && isCoordinator && businessAuditorLinkedDocsSidebar && (
          <div className="auditor-workspace-linked-files">
            {businessAuditorLinkedDocsSidebar}
          </div>
        )}
        {/* Accountant / Business Auditor coordinator: document summary when a linked file is selected */}
        {showLinkedDocSummaryPanel && (
          <div className="accountant-doc-summary-fill">
            {selectedVendorCategoryId ? (
              /* ── Vendor category panel ──────────────────────────────────── */
              <div className="bank-stmt-cal-panel">
                <div className="bank-stmt-cal-header">
                  <span className="bank-stmt-cal-title">
                    {(VENDOR_CATEGORIES.find((c) => c.id === selectedVendorCategoryId) || { label: 'OTHER' }).label} Invoices
                  </span>
                  <span className="bank-stmt-cal-subtitle">
                    Click an invoice to view its summary, or add a new one
                  </span>
                </div>

                <div className="bank-stmt-cal-grid vendor-cat-cal-grid">
                  {(vendorDocsByCategory[selectedVendorCategoryId] || []).map((doc) => {
                    const isSelected = selectedAccountantDocId === doc.id;
                    const raw = doc.label || doc.file_path || '';
                    const short = raw.length > 26 ? raw.slice(0, 23) + '…' : raw;
                    return (
                      <button
                        key={doc.id}
                        type="button"
                        className={`bank-stmt-cal-cell has-file${isSelected ? ' is-selected' : ''}`}
                        title={raw}
                        onClick={() =>
                          setSelectedAccountantDocId((prev) => (prev === doc.id ? null : doc.id))
                        }
                      >
                        <span className="bank-stmt-cal-cell-mon vendor-cell-label">{short}</span>
                        <span className="bank-stmt-cal-cell-check">✓</span>
                      </button>
                    );
                  })}

                  {/* Add New tile */}
                  <button
                    type="button"
                    className="bank-stmt-cal-cell bank-stmt-cal-add"
                    onClick={() => vendorAddInputRef.current?.click()}
                    disabled={addingDocument}
                    title="Add new invoice"
                  >
                    <span className="bank-stmt-cal-add-icon">{addingDocument ? '…' : '+'}</span>
                    <span className="bank-stmt-cal-cell-mon vendor-cell-label">Add New</span>
                  </button>
                  <input
                    ref={vendorAddInputRef}
                    type="file"
                    accept=".pdf,.xlsx,.xls,.png,.jpg,.jpeg,.doc,.docx,.csv"
                    style={{ display: 'none' }}
                    onChange={onVendorAddFileChange}
                  />
                </div>

                {/* Detail / summary section for selected vendor doc */}
                {selectedAccountantDocId &&
                (vendorDocsByCategory[selectedVendorCategoryId] || []).some((d) => d.id === selectedAccountantDocId) ? (
                  <>
                    <div className="bank-stmt-cal-divider" />
                    <div className="bank-stmt-cal-detail">
                      <div className="bank-stmt-cal-detail-header">
                        <span className="bank-stmt-cal-detail-title">
                          {(vendorDocsByCategory[selectedVendorCategoryId] || []).find((d) => d.id === selectedAccountantDocId)?.label || ''}
                        </span>
                        <div className="bank-stmt-cal-detail-actions">
                          <a
                            href={`/opo/document/${selectedAccountantDocId}`}
                            target="_blank"
                            rel="noreferrer"
                            className="auditor-doc-view-link bank-stmt-cal-view-pdf"
                            title="Open original document"
                            onClick={(e) => openDocumentViewerTab(selectedAccountantDocId, e)}
                          >
                            View
                          </a>
                          {isActiveLinkedDocPdf && (
                            <label className="auditor-summary-refresh-extract" title="Re-scan PDF with OCR">
                              <input
                                type="checkbox"
                                checked={summaryRefreshExtract}
                                onChange={(e) => setSummaryRefreshExtract(e.target.checked)}
                                disabled={summaryLoading}
                              />
                              Re-scan PDF (OCR)
                            </label>
                          )}
                          <button
                            type="button"
                            className="subagent-details-summary-btn accountant-doc-summary-btn"
                            onClick={handleGenerateSummary}
                            disabled={summaryLoading}
                          >
                            {summaryLoading ? 'Generating…' : 'Generate summary'}
                          </button>
                        </div>
                      </div>

                      {isActiveLinkedDocPdf && (
                        <div className="auditor-linked-extract-hint">
                          {linkedExtractLoading ? (
                            <span>Checking file…</span>
                          ) : linkedExtractStatus?.textLength > 0 ? (
                            <span>
                              {linkedExtractStatus.textLength.toLocaleString()} chars extracted
                              {linkedExtractStatus.source ? ` (${linkedExtractStatus.source})` : ''}
                              {linkedExtractStatus.staleCache ? ' — file changed; tick Re-scan to refresh' : ''}
                            </span>
                          ) : (
                            <span className="auditor-linked-extract-warn">
                              No text extracted yet — click Generate summary to run OCR.
                            </span>
                          )}
                        </div>
                      )}

                      <div className="accountant-doc-summary-scroll">
                        {summaryError && (
                          <div className="auditor-summary-error">
                            <strong>Extract / summary failed:</strong>
                            <span>{summaryError}</span>
                          </div>
                        )}
                        {documentSummary ? (
                          <>
                            <div className="subagent-details-summary-markdown">
                              <ReactMarkdown>{documentSummary}</ReactMarkdown>
                            </div>
                            {summaryCreatedAt && (
                              <div className="subagent-details-summary-meta">
                                Generated {new Date(summaryCreatedAt).toLocaleString()}
                              </div>
                            )}
                          </>
                        ) : !summaryError ? (
                          <p className="subagent-details-summary-empty">
                            No summary yet. Click &quot;Generate summary&quot; to analyse this document.
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="bank-stmt-cal-placeholder">
                    Select an invoice above to view its summary, or click + Add New to upload.
                  </p>
                )}
              </div>
            ) : (
              /* ── Regular document summary panel ────────────────────────── */
              <>
                <div className="accountant-doc-summary-header">
                  <span className="accountant-doc-summary-label">Document summary</span>
                  <div className="accountant-doc-summary-header-actions">
                    {isActiveLinkedDocPdf && (
                      <label className="auditor-summary-refresh-extract" title="Ignore cached extract, re-run PDF text + OCR, then summarize">
                        <input
                          type="checkbox"
                          checked={summaryRefreshExtract}
                          onChange={(e) => setSummaryRefreshExtract(e.target.checked)}
                          disabled={summaryLoading}
                        />
                        Re-scan PDF (OCR)
                      </label>
                    )}
                    <button
                      type="button"
                      className="subagent-details-summary-btn accountant-doc-summary-btn"
                      onClick={handleGenerateSummary}
                      disabled={summaryLoading}
                    >
                      {summaryLoading ? 'Generating…' : 'Generate summary'}
                    </button>
                  </div>
                </div>
                {isActiveLinkedDocPdf && (
                  <div className="auditor-linked-extract-hint">
                    {linkedExtractLoading ? (
                      <span>Checking extracted text…</span>
                    ) : linkedExtractStatus && linkedExtractStatus.fileExists === false ? (
                      <span className="auditor-linked-extract-warn">File missing on server — cannot extract text.</span>
                    ) : linkedExtractStatus && linkedExtractStatus.textLength > 0 ? (
                      <span>
                        Cached extract: {linkedExtractStatus.textLength.toLocaleString()} chars
                        {linkedExtractStatus.source ? ` (${linkedExtractStatus.source})` : ''}
                        {linkedExtractStatus.staleCache ? ' — file changed; tick Re-scan to refresh' : ''}
                      </span>
                    ) : (
                      <span className="auditor-linked-extract-warn">
                        No substantial text in DB yet — first summary will run OCR if the PDF is scanned. Tick &quot;Re-scan PDF (OCR)&quot; if a prior run cached empty text.
                      </span>
                    )}
                  </div>
                )}
                <div className="accountant-doc-summary-scroll">
                  {summaryError && (
                    <div className="auditor-summary-error">
                      <strong>Extract / summary failed:</strong>
                      <span>{summaryError}</span>
                    </div>
                  )}
                  {documentSummary ? (
                    <>
                      <div className="subagent-details-summary-markdown">
                        <ReactMarkdown>{documentSummary}</ReactMarkdown>
                      </div>
                      {summaryCreatedAt && (
                        <div className="subagent-details-summary-meta">Generated {new Date(summaryCreatedAt).toLocaleString()}</div>
                      )}
                    </>
                  ) : !summaryError ? (
                    <p className="subagent-details-summary-empty">
                      {selectedSubAgentId === 'accountant'
                        ? 'No summary yet. Click "Generate summary" or ask the Accountant to analyze the document.'
                        : 'No summary yet. Click "Generate summary" to have the Business Auditor (Qwen) summarize this linked file.'}
                    </p>
                  ) : null}
                </div>
              </>
            )}
          </div>
        )}
        {/* OptiPrime T2: replace the generic sub-agent details panel with the T2 workflow panel */}
        {selectedSubAgentId && selectedSubAgent && selectedSubAgent.t2 && !showLinkedDocSummaryPanel && (
          <T2AgentPanel />
        )}
        {/* AGM: first annual general meeting workflow */}
        {selectedSubAgentId && selectedSubAgent && selectedSubAgent.agm && !showLinkedDocSummaryPanel && (
          <AGMAgentPanel />
        )}
        {/* GST & PST: sales tax sub-auditor */}
        {selectedSubAgentId && selectedSubAgent && selectedSubAgent.gstPst && !showLinkedDocSummaryPanel && (
          <GstPstAgentPanel />
        )}
        {/* Help: deterministic grounded Q&A over the Auditor records */}
        {selectedSubAgentId && selectedSubAgent && selectedSubAgent.help && !showLinkedDocSummaryPanel && (
          <AuditorHelpPanel />
        )}
        {/* Corporate Records: minute-book shelves (Resolutions, Board minutes, Schedules, …) */}
        {selectedSubAgentId === 'corporate_records' && !showLinkedDocSummaryPanel && (
          <CorporateRecordsPanel />
        )}
        {/* For non-Accountant sub-agents (excluding T2, AGM, GST/PST, Help & Corporate Records)
            or Accountant with no document selected */}
        {selectedSubAgentId && selectedSubAgent
          && !selectedSubAgent.t2 && !selectedSubAgent.agm && !selectedSubAgent.gstPst && !selectedSubAgent.help
          && selectedSubAgentId !== 'corporate_records'
          && !showLinkedDocSummaryPanel && !showAccountantInstructions && (
          <SubAgentDetailsPanel
            subAgent={selectedSubAgent}
            documentSummary={documentSummary}
            summaryCreatedAt={summaryCreatedAt}
            onGenerateSummary={handleGenerateSummary}
            summaryLoading={summaryLoading}
            showDocumentSummary={false}
          />
        )}
        {/* Coordinator (Business Auditor): tab system swaps between Compliance / Tasks / Conversation */}
        {!showLinkedDocSummaryPanel && isCoordinator && (
          <div className="auditor-coord-tabs-wrap">
            <div className="auditor-coord-tabs" role="tablist" aria-label="Business Auditor sections">
              <button
                type="button"
                role="tab"
                aria-selected={coordinatorTab === 'compliance'}
                className={`auditor-coord-tab${coordinatorTab === 'compliance' ? ' is-active' : ''}`}
                onClick={() => setCoordinatorTab('compliance')}
              >
                Document compliance
                {complianceScan?.summary && (
                  <span className="auditor-coord-tab-badge">{complianceScan.summary.compliancePercent}%</span>
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={coordinatorTab === 'tasks'}
                className={`auditor-coord-tab${coordinatorTab === 'tasks' ? ' is-active' : ''}`}
                onClick={() => setCoordinatorTab('tasks')}
              >
                Task checklist
                <span className="auditor-coord-tab-badge">
                  {Object.values(byArea).reduce((n, arr) => n + (arr ? arr.length : 0), 0)}
                </span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={coordinatorTab === 'chat'}
                className={`auditor-coord-tab${coordinatorTab === 'chat' ? ' is-active' : ''}`}
                onClick={() => setCoordinatorTab('chat')}
              >
                Conversation
                {history.length > 0 && (
                  <span className="auditor-coord-tab-badge">{history.length}</span>
                )}
              </button>
            </div>

            {coordinatorTab === 'compliance' && (
              <div className="auditor-coord-tab-body auditor-coord-tab-body--compliance">
                {pageMode && statusSnapshot && (
                  <div className="auditor-command-center-cards">
                    <button
                      type="button"
                      className="auditor-cc-card"
                      onClick={() => setCoordinatorTab('compliance')}
                    >
                      <span className="auditor-cc-card-label">COMPLIANCE</span>
                      <span className="auditor-cc-card-value">
                        {statusSnapshot.compliance?.summary?.compliancePercent != null
                          ? `${statusSnapshot.compliance.summary.compliancePercent}%`
                          : '—'}
                      </span>
                      <span className="auditor-cc-card-foot">Document checklist</span>
                    </button>
                    <button
                      type="button"
                      className="auditor-cc-card"
                      onClick={() => statusSnapshot.onNavigateAgent?.('gst-pst')}
                    >
                      <span className="auditor-cc-card-label">GST</span>
                      <span className="auditor-cc-card-value">
                        {statusSnapshot.gstSummary?.daysToDeadline == null
                          ? '—'
                          : statusSnapshot.gstSummary.daysToDeadline < 0
                            ? `${Math.abs(statusSnapshot.gstSummary.daysToDeadline)}d overdue`
                            : `${statusSnapshot.gstSummary.daysToDeadline}d`}
                      </span>
                      <span className="auditor-cc-card-foot">Open GST &amp; PST</span>
                    </button>
                    <button
                      type="button"
                      className="auditor-cc-card"
                      onClick={() => statusSnapshot.onNavigateAgent?.('t2')}
                    >
                      <span className="auditor-cc-card-label">T2</span>
                      <span className="auditor-cc-card-value" style={{ fontSize: '1rem' }}>
                        {String(
                          statusSnapshot.t2Summary?.lastFiled?.status ||
                            statusSnapshot.t2Summary?.currentPeriod?.status ||
                            '—'
                        ).replace(/_/g, ' ')}
                      </span>
                      <span className="auditor-cc-card-foot">Open OptiPrime T2</span>
                    </button>
                    <button
                      type="button"
                      className="auditor-cc-card"
                      onClick={() => setCoordinatorTab('tasks')}
                    >
                      <span className="auditor-cc-card-label">TASKS</span>
                      <span className="auditor-cc-card-value">
                        {(statusSnapshot.tasksFlat || []).filter((t) => t.status !== 'complete').length}
                      </span>
                      <span className="auditor-cc-card-foot">
                        {(statusSnapshot.tasksFlat || []).filter((t) => t.status === 'needs_attention').length} need attention
                      </span>
                    </button>
                  </div>
                )}
                <div className={`auditor-corp-info-panel${pageMode ? ' auditor-corp-info-panel--compact' : ''}`}>
                  <div className="auditor-corp-info-header">
                    <span className="auditor-corp-info-title">CORP INFO — Corporation profile</span>
                    <span className="auditor-corp-info-subtitle">Line 299 NAICS · Line 750 jurisdiction · e-file metadata</span>
                  </div>
                  {corpInfoLoading && !corpInfo ? (
                    <p className="auditor-corp-info-muted">Loading corporation profile…</p>
                  ) : corpInfo ? (
                    <>
                      <p className="auditor-corp-info-explainer">
                        Certified T2 software collects a <strong>CORP INFO</strong> worksheet that is not part of the CRA fillable T2 PDF.
                        Values below come from <code className="auditor-inline-code">cra_prefill_constants.json</code>.
                        Open the <strong>OptiPrime T2</strong> sub-agent for the full schedule workflow.
                      </p>
                      <dl className="auditor-corp-info-grid">
                        <dt>Line 299 — NAICS</dt>
                        <dd>
                          <strong>{corpInfo.line299?.naicsCode}</strong>
                          {' '}
                          {corpInfo.line299?.naicsTitle}
                        </dd>
                        <dt>Activity</dt>
                        <dd>{corpInfo.line299?.primaryDescription}</dd>
                        <dt>Line 750 — Jurisdiction</dt>
                        <dd>{corpInfo.line750}</dd>
                        <dt>Entity</dt>
                        <dd>
                          {corpInfo.entity?.legalName}
                          {' · '}
                          BN {corpInfo.entity?.businessNumber}
                          {' · '}
                          FYE {corpInfo.entity?.fiscalYearEnd?.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}
                        </dd>
                      </dl>
                      <div className="auditor-corp-info-actions">
                        {corpInfo.pdf?.present && corpInfo.pdf.viewUrl ? (
                          <a
                            href={corpInfo.pdf.viewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="auditor-corp-info-btn auditor-corp-info-btn--view"
                          >
                            View CORP INFO PDF
                          </a>
                        ) : (
                          <span className="auditor-corp-info-missing">PDF not generated yet</span>
                        )}
                        <button
                          type="button"
                          className="auditor-corp-info-btn auditor-corp-info-btn--generate"
                          onClick={generateCorpInfoPdf}
                          disabled={corpInfoGenerating}
                        >
                          {corpInfoGenerating ? 'Generating…' : corpInfo.pdf?.present ? 'Regenerate PDF' : 'Generate PDF'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="auditor-corp-info-muted">CORP INFO profile unavailable.</p>
                  )}
                </div>
                {!complianceScan ? (
                  <div className="auditor-coord-empty">
                    {complianceScanLoading ? 'Loading compliance scan…' : 'No compliance scan available.'}
                  </div>
                ) : (
                  <div className="auditor-compliance-panel auditor-compliance-panel--full">
                    <div className="auditor-compliance-header">
                  <span className="auditor-compliance-title">Document compliance</span>
                  <div className="auditor-compliance-header-right">
                    <span className="auditor-compliance-pct">{complianceScan.summary.compliancePercent}%</span>
                    <span className="auditor-compliance-count">({complianceScan.summary.present}/{complianceScan.summary.total})</span>
                    <button
                      type="button"
                      className="auditor-compliance-refresh"
                      onClick={fetchComplianceScan}
                      disabled={complianceScanLoading}
                      title="Refresh scan"
                    >
                      {complianceScanLoading ? '↻' : '↻'}
                    </button>
                  </div>
                </div>
                <div className="auditor-compliance-bar-track">
                  <div
                    className="auditor-compliance-bar-fill"
                    style={{ width: `${complianceScan.summary.compliancePercent}%` }}
                  />
                </div>
                {/* Section tabs — primary filter */}
                <div className="auditor-compliance-section-row">
                  <button
                    type="button"
                    className={`auditor-compliance-section-btn${complianceSectionFilter === 'all' ? ' active' : ''}`}
                    onClick={() => { setComplianceSectionFilter('all'); setCompliancePriorityFilter('all'); }}
                  >All</button>
                  {(complianceScan.sections || []).map((sec) => {
                    const secItems = (complianceScan.items || []).filter(i => i.section === sec);
                    const secMissing = secItems.filter(i => i.status === 'missing').length;
                    const shortLabel = sec
                      .replace('Resolution to Purchase DGX Spark', 'DGX Spark Resolution')
                      .replace('SR&ED (R&D Tax Credits)', 'SR&ED')
                      .replace('Corporate Registry and Compliance', 'Corp. Registry')
                      .replace('Share Register and Capital', 'Share Register')
                      .replace('CRA and Tax Filings', 'CRA & Tax')
                      .replace('Receipts and Audit Trail', 'Receipts')
                      .replace('Setup and Incorporation', 'Setup & Incorp.')
                      .replace('Assets and Depreciation', 'Assets & Depr.')
                      .replace('Investor Readiness', 'Investor')
                      .replace('Financial Records', 'Financials')
                      .replace('Minute Book', 'Minute Book');
                    return (
                      <button
                        key={sec}
                        type="button"
                        className={`auditor-compliance-section-btn${complianceSectionFilter === sec ? ' active' : ''}${secMissing > 0 ? ' has-missing' : ''}`}
                        onClick={() => { setComplianceSectionFilter(sec); setCompliancePriorityFilter('all'); }}
                        title={sec}
                      >
                        {shortLabel}{secMissing > 0 ? <span className="auditor-compliance-section-badge">{secMissing}</span> : null}
                      </button>
                    );
                  })}
                </div>
                {/* Priority / status secondary filter */}
                <div className="auditor-compliance-filter-row">
                  {['all', 'critical', 'required', 'recommended', 'present'].map((f) => {
                    const scope = complianceSectionFilter === 'all'
                      ? (complianceScan.items || [])
                      : (complianceScan.items || []).filter(i => i.section === complianceSectionFilter);
                    const count = f === 'all' ? scope.length
                      : f === 'present' ? scope.filter(i => i.status === 'present').length
                      : scope.filter(i => i.priority === f && i.status === 'missing').length;
                    const label = f === 'all' ? `All (${count})`
                      : f === 'present' ? `Present (${count})`
                      : f === 'critical' ? `Critical missing (${count})`
                      : f === 'required' ? `Required missing (${count})`
                      : `Recommended (${count})`;
                    return (
                      <button
                        key={f}
                        type="button"
                        className={`auditor-compliance-filter-btn${compliancePriorityFilter === f ? ' active' : ''}`}
                        onClick={() => setCompliancePriorityFilter(f)}
                      >{label}</button>
                    );
                  })}
                </div>
                <input
                  ref={complianceFileInputRef}
                  type="file"
                  accept=".pdf,.xlsx,.xls,.doc,.docx"
                  className="auditor-doc-input-hidden"
                  onChange={onComplianceFileChange}
                />
                <div className="auditor-compliance-items">
                  {(complianceScan.items || [])
                    .filter((item) => {
                      if (complianceSectionFilter !== 'all' && item.section !== complianceSectionFilter) return false;
                      if (compliancePriorityFilter === 'all') return true;
                      if (compliancePriorityFilter === 'present') return item.status === 'present';
                      return item.status === 'missing' && item.priority === compliancePriorityFilter;
                    })
                    .sort((a, b) => {
                      if (a.status !== b.status) return a.status === 'missing' ? -1 : 1;
                      const po = PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority);
                      if (po !== 0) return po;
                      return (a.document || '').localeCompare(b.document || '', undefined, { sensitivity: 'base' });
                    })
                    .map((item, i) => {
                      const itemNote = complianceNotes[item.expectedPath];
                      const hasNote = !!(itemNote && itemNote.note);
                      return (
                        <div key={i} className={`auditor-compliance-item auditor-compliance-item-${item.status} auditor-compliance-item-p-${item.priority}`}>
                          <span className={`auditor-compliance-badge auditor-compliance-badge-${item.priority}`}>{item.priority}</span>
                          <span className="auditor-compliance-doc-col">
                            {item.status === 'present' && item.actualFile ? (
                              <a
                                href={`/api/opo/auditor/compliance-document/view?file=${encodeURIComponent(item.actualFile)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="auditor-compliance-doc-name auditor-compliance-doc-link"
                                title={`View ${item.document} — ${item.description}`}
                              >
                                {item.document}
                              </a>
                            ) : (
                              <span className="auditor-compliance-doc-name" title={item.description}>{item.document}</span>
                            )}
                            {hasNote && (
                              <span className="auditor-compliance-note-snippet" title={itemNote.note}>
                                {itemNote.note.length > 60 ? itemNote.note.slice(0, 57) + '…' : itemNote.note}
                              </span>
                            )}
                          </span>
                          <span className="auditor-compliance-owner">{item.owner}</span>
                          <span className={`auditor-compliance-status auditor-compliance-status-${item.status}`}>{item.status}</span>
                          <span className="auditor-compliance-actions">
                            <button
                              type="button"
                              className={`auditor-compliance-note-btn${hasNote ? ' auditor-compliance-note-btn-active' : ''}`}
                              onClick={() => setNoteModal({ item, draft: hasNote ? itemNote.note : '', saving: false, error: null })}
                              title={hasNote ? `Note: ${itemNote.note}` : 'Add a note for this document'}
                            >
                              {hasNote ? '📝' : 'Note'}
                            </button>
                            {item.status === 'missing' && (
                              <button
                                type="button"
                                className="auditor-compliance-upload-btn"
                                onClick={() => onComplianceUploadClick(item)}
                                title={`Upload for ${item.document}`}
                              >
                                Upload
                              </button>
                            )}
                            {item.status === 'present' && (
                              <button
                                type="button"
                                className="auditor-compliance-edit-btn"
                                onClick={() => onComplianceEditClick(item)}
                                title={`Replace ${item.document}`}
                              >
                                Edit
                              </button>
                            )}
                          </span>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
                )}
              </div>
            )}

            {coordinatorTab === 'tasks' && (
              <div className="auditor-coord-tab-body auditor-coord-tab-body--tasks">
                <div className="auditor-task-summary auditor-task-summary--full">
                  <h3>Task checklist</h3>
                  {loadingTasks ? (
                    <div className="auditor-task-list">Loading…</div>
                  ) : areas.length === 0 ? (
                    <div className="auditor-coord-empty">No tasks recorded yet.</div>
                  ) : (
                    <div className="auditor-task-list">
                      {areas.map((area) => (
                        <div key={area} className="auditor-task-area">
                          <div className="auditor-task-area-title">{area}</div>
                          {(byArea[area] || [])
                            .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status))
                            .map((t) => (
                              <div
                                key={t.id}
                                className="auditor-task-item"
                                onClick={() => updateTaskStatus(t.id, t.status)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && updateTaskStatus(t.id, t.status)}
                              >
                                <span className="auditor-task-item-content">{t.item}</span>
                                <span className={`auditor-task-item-status ${(t.status || 'pending').replace(/\s/g, '_')}`}>
                                  {(t.status || 'pending').replace(/_/g, ' ')}
                                </span>
                              </div>
                            ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {coordinatorTab === 'chat' && (
              <div className="auditor-coord-tab-body auditor-coord-tab-body--chat">
                <div className="auditor-chat-messages">
                  {loadingHistory ? (
                    <div className="auditor-chat-msg assistant">Loading history…</div>
                  ) : (
                    <>
                      {history.map((m, i) => (
                        <div key={i} className={`auditor-chat-msg ${m.role}`}>
                          {m.content}
                          {m.created_at && <div className="auditor-msg-meta">{new Date(m.created_at).toLocaleString()}</div>}
                        </div>
                      ))}
                      {streamingMessage && (
                        <div className="auditor-chat-msg assistant">
                          {streamingMessage}
                          <span className="auditor-chat-typing">▌</span>
                        </div>
                      )}
                      {streaming && !streamingMessage && <div className="auditor-chat-typing">Typing…</div>}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sub-agent (non-coordinator): conversation view as before, no tabs.
            OptiPrime T2, AGM, GST/PST, and Help have their own panel UI — skip the global chat here.
            In pageMode, accountant/generic chat lives in a collapsible Ask dock. */}
        {!showLinkedDocSummaryPanel && !isCoordinator && !selectedSubAgent?.t2 && !selectedSubAgent?.agm && !selectedSubAgent?.gstPst && !selectedSubAgent?.help && (
          <div className="auditor-resizable-area">
            {showAccountantInstructions && (
              <AccountantInstructionsPanel
                companyCostRegisterDoc={companyCostRegisterDoc}
                onOpenWorkbook={() => {
                  if (companyCostRegisterDoc?.id) {
                    window.open(`/opo/document/${companyCostRegisterDoc.id}`, '_blank');
                  }
                }}
              />
            )}
            {(!pageMode || chatDockOpen) && (history.length > 0 || streamingMessage || streaming || loadingHistory || !showAccountantInstructions) && (
            <div className="auditor-chat-messages">
              {loadingHistory ? (
                <div className="auditor-chat-msg assistant">Loading history…</div>
              ) : (
                <>
                  {history.map((m, i) => (
                    <div key={i} className={`auditor-chat-msg ${m.role}`}>
                      {m.content}
                      {m.created_at && <div className="auditor-msg-meta">{new Date(m.created_at).toLocaleString()}</div>}
                    </div>
                  ))}
                  {streamingMessage && (
                    <div className="auditor-chat-msg assistant">
                      {streamingMessage}
                      <span className="auditor-chat-typing">▌</span>
                    </div>
                  )}
                  {streaming && !streamingMessage && <div className="auditor-chat-typing">Typing…</div>}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
            )}
          </div>
        )}

        {/* pageMode Ask dock toggle for accountant / generic agents */}
        {pageMode && !showLinkedDocSummaryPanel && !isCoordinator && !selectedSubAgent?.t2 && !selectedSubAgent?.agm && !selectedSubAgent?.gstPst && !selectedSubAgent?.help && (
          <div className="auditor-ask-dock">
            <button
              type="button"
              className={`auditor-ask-dock-toggle${chatDockOpen ? ' is-open' : ''}`}
              onClick={() => setChatDockOpen((v) => !v)}
              aria-expanded={chatDockOpen}
            >
              {chatDockOpen ? '▾ Hide Ask' : `▸ Ask the ${selectedSubAgent?.name || 'agent'}`}
            </button>
            {chatDockOpen && (
              <div className="auditor-query-bar auditor-query-bar--dock">
                <textarea
                  ref={queryBarRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={`Ask the ${selectedSubAgent?.name || 'agent'}… (Enter to send, Shift+Enter for new line)`}
                  disabled={streaming}
                  rows={2}
                />
                <div className="auditor-query-hint">Enter to send · Shift+Enter for new line</div>
              </div>
            )}
          </div>
        )}

        {/* Query bar: modal mode, or coordinator Conversation tab.
            Suppressed for OptiPrime T2, AGM, GST/PST, and Help (each has its own panel UI).
            pageMode accountant/generic uses Ask dock above. */}
        {!showLinkedDocSummaryPanel && !selectedSubAgent?.t2 && !selectedSubAgent?.agm && !selectedSubAgent?.gstPst && !selectedSubAgent?.help && (!isCoordinator || coordinatorTab === 'chat') && !(pageMode && !isCoordinator) && (
          <div className="auditor-query-bar">
            <textarea
              ref={queryBarRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                selectedSubAgent
                  ? `Ask the ${selectedSubAgent.name}… (Enter to send, Shift+Enter for new line)`
                  : 'Ask the Business Auditor… (Enter to send, Shift+Enter for new line)'
              }
              disabled={streaming}
              rows={2}
            />
            <div className="auditor-query-hint">Enter to send · Shift+Enter for new line</div>
          </div>
        )}
      </div>

      {/* ── Compliance item note modal ─────────────────────────────────────── */}
      {noteModal && (
        <div className="note-modal-overlay" onClick={() => !noteModal.saving && setNoteModal(null)}>
          <div className="note-modal" onClick={(e) => e.stopPropagation()}>
            <div className="note-modal-header">
              <span className="note-modal-title">Note — {noteModal.item.document}</span>
              <button
                type="button"
                className="note-modal-close"
                onClick={() => setNoteModal(null)}
                disabled={noteModal.saving}
                aria-label="Close"
              >✕</button>
            </div>
            <div className="note-modal-hint">
              Short-term memory for this document. The LLM will read this note before assessing compliance — use it to explain timing, exceptions, or context.
            </div>
            <textarea
              className="note-modal-textarea"
              placeholder="e.g. This document is only due after April 30, 2026 filing deadline…"
              value={noteModal.draft}
              onChange={(e) => setNoteModal((m) => ({ ...m, draft: e.target.value }))}
              disabled={noteModal.saving}
              rows={5}
              autoFocus
            />
            {noteModal.error && (
              <div className="note-modal-error">Error: {noteModal.error}</div>
            )}
            <div className="note-modal-actions">
              <button
                type="button"
                className="note-modal-cancel-btn"
                onClick={() => setNoteModal(null)}
                disabled={noteModal.saving}
              >
                Cancel
              </button>
              {noteModal.draft.trim() !== '' && (noteModal.item.expectedPath ? (complianceNotes[noteModal.item.expectedPath]?.note || '') : '') !== '' && (
                <button
                  type="button"
                  className="note-modal-clear-btn"
                  onClick={() => saveComplianceNote(noteModal.item.expectedPath, '')}
                  disabled={noteModal.saving}
                >
                  Clear note
                </button>
              )}
              <button
                type="button"
                className="note-modal-save-btn"
                onClick={() => saveComplianceNote(noteModal.item.expectedPath, noteModal.draft)}
                disabled={noteModal.saving}
              >
                {noteModal.saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Upload progress modal ──────────────────────────────────────────── */}
      {uploadModal && (
        <div className="upload-modal-overlay" onClick={uploadModal.done ? () => setUploadModal(null) : undefined}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <span className="upload-modal-title">Uploading: {uploadModal.item.document}</span>
              {uploadModal.done && (
                <button type="button" className="upload-modal-close" onClick={() => setUploadModal(null)}>✕</button>
              )}
            </div>
            <div className="upload-modal-owner">Owner: {uploadModal.item.owner} · {uploadModal.item.priority}</div>

            {/* Stage progress */}
            <div className="upload-modal-stages">
              {uploadModal.stages.map((s, i) => (
                <div key={i} className={`upload-modal-stage upload-modal-stage-${s.stage}`}>
                  <span className="upload-modal-stage-icon">
                    {s.stage === 'error' ? '✕' : s.stage === 'done' ? '✓' : '✓'}
                  </span>
                  <span className="upload-modal-stage-msg">{s.message}</span>
                </div>
              ))}
              {!uploadModal.done && (
                <div className="upload-modal-stage upload-modal-stage-pending">
                  <span className="upload-modal-spinner" />
                  <span className="upload-modal-stage-msg">
                    {uploadModal.stages.length === 0 && 'Uploading…'}
                    {uploadModal.stages.length === 1 && 'Extracting content…'}
                    {uploadModal.stages.length === 2 && 'Sending to OptiPrime…'}
                    {uploadModal.stages.length === 3 && 'OptiPrime is reviewing…'}
                    {uploadModal.stages.length >= 4 && !uploadModal.done && 'Mapping document to agents…'}
                    {uploadModal.stages.length >= 4 && uploadModal.done && 'Done'}
                  </span>
                </div>
              )}
            </div>

            {/* OptiPrime streaming output */}
            {(uploadModal.qwenText || uploadModal.stages.find(s => s.stage === 'verifying')) && (
              <div className="upload-modal-qwen">
                <div className="upload-modal-qwen-header">
                  <span className="upload-modal-qwen-label">OptiPrime verification</span>
                  {!uploadModal.done && <span className="upload-modal-spinner" />}
                </div>
                <div className="upload-modal-qwen-body">
                  {uploadModal.qwenText
                    ? uploadModal.qwenText.split('\n').map((line, i) => <p key={i}>{line}</p>)
                    : <span className="upload-modal-qwen-waiting">OptiPrime is reading the document…</span>
                  }
                  {!uploadModal.done && <span className="upload-modal-cursor">▌</span>}
                </div>
              </div>
            )}

            {uploadModal.error && (
              <div className="upload-modal-error">Error: {uploadModal.error}</div>
            )}

            {uploadModal.done && !uploadModal.error && (
              <div className="upload-modal-success">
                Document saved and verified. The compliance list has been updated.
              </div>
            )}

            {uploadModal.done && (
              <button type="button" className="upload-modal-dismiss" onClick={() => setUploadModal(null)}>
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditorBusinessPanel;
